import {
  getAvailabilityState,
  parseMf16Ids,
  parseMf20Stock,
  parseMf34PreorderAllowed,
  parseMf36StockSynced,
  getSelectedAvailabilityBakeryId,
  getSelectedPickupDay,
} from '@theme/availability';

/**
 * Scrub a string sourced from a Liquid `| t`-rendered data-* attribute. Returns
 * an empty string when the value is blank or matches Shopify's
 * `Translation missing: <locale>.<key>` sentinel that the `| t` filter emits
 * verbatim for absent translation keys. Lets every downstream `value || fallback`
 * short-circuit correctly instead of rendering the sentinel as UI copy.
 *
 * Pair with the `tt.liquid` snippet (Liquid-side fallback at render time) — this
 * is the JS-side defence for data-attribute templates we can't run through `tt`
 * because they need `| t: var: 'value'` interpolation.
 *
 * @param {string | undefined | null} value
 * @returns {string}
 */
function safeTranslation(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (/^Translation missing/i.test(trimmed)) return '';
  return trimmed;
}

/**
 * @param {HTMLElement} formButtons
 * @returns {HTMLElement|null}
 */
function getBuyButtonsAtcWrapper(formButtons) {
  const atcComponent = formButtons.querySelector('add-to-cart-component');
  if (!atcComponent) return null;

  let el = /** @type {HTMLElement} */ (atcComponent);
  while (el.parentElement && el.parentElement !== formButtons) {
    el = el.parentElement;
  }

  return el;
}

/**
 * Reads the selected variant's `available` flag from the variant-picker JSON script.
 *
 * @param {HTMLElement} blockEl
 * @returns {boolean | null} `null` when unknown
 */
function getVariantAvailableFromBlock(blockEl) {
  const root =
    blockEl.closest('[data-product-grid-content]') ?? blockEl.closest('product-form-component') ?? blockEl;
  const script = root.querySelector('variant-picker script[type="application/json"]');
  if (!script?.textContent) return null;

  try {
    const variant = JSON.parse(script.textContent);
    return typeof variant?.available === 'boolean' ? variant.available : null;
  } catch {
    return null;
  }
}

/**
 * Apply buy-button state for a single block element using MF16 / MF20
 * metafield data embedded as data attributes by blocks/buy-buttons.liquid.
 *
 * State rules (`getAvailabilityState` in assets/availability.js):
 *   available   → ATC button enabled (and Shopify variant.available !== false)
 *   sold_out    → ATC button disabled with sold-out label
 *   unavailable → ATC button disabled with unavailable label
 *
 * @param {HTMLElement} blockEl
 * @param {boolean | null} [variantAvailable] - From variant:update or variant-picker JSON
 */
function applyBuyButtonsState(blockEl, variantAvailable = null) {
  const stateApi = /** @type {any} */ (window).StoreSelectionState;
  const selection =
    stateApi && typeof stateApi.getSelection === 'function' ? stateApi.getSelection() : null;

  const formButtons = /** @type {HTMLElement|null} */ (blockEl.querySelector('.product-form-buttons'));
  if (!formButtons) return;

  const placeholderEl = /** @type {HTMLElement|null} */ (
    blockEl.querySelector('[data-pdp-buy-buttons-placeholder]')
  );
  const selectBakeryEl = /** @type {HTMLElement|null} */ (blockEl.querySelector('[data-pdp-select-bakery]'));
  const atcWrapper = getBuyButtonsAtcWrapper(formButtons);
  const quantityEl = /** @type {HTMLElement|null} */ (formButtons.querySelector('.quantity-selector'));
  const hasStore = Boolean(selection && selection.storeId);

  blockEl.dataset.buyButtonsInitialized = 'true';

  if (placeholderEl) placeholderEl.hidden = true;
  if (selectBakeryEl) selectBakeryEl.hidden = hasStore;
  if (atcWrapper) atcWrapper.hidden = !hasStore;

  if (!hasStore || !atcWrapper) {
    // No bakery selected → "Select my bakery" CTA is the only actionable
    // control. Lock the quantity stepper so it can't be tabbed/clicked into
    // a non-default state while the ATC is hidden — `setQuantityControlsDisabled`
    // also stamps `data-disabled="true"` on the wrapper for the CSS dimming
    // that already exists for the sold_out / unavailable post-selection cases.
    setQuantityControlsDisabled(quantityEl, true);
    // Preparations + heading only change for the MF34 preorder-blocked case
    // below, which requires a selection — make sure clearing the bakery
    // restores them.
    setPreparationControlsDisabled(blockEl, false);
    setBuyButtonsHeadingHidden(blockEl, false);
    // No bakery selected → the "Select my bakery" CTA must stay visible, so the
    // whole-form MF16-miss hide (below) must not apply here.
    blockEl.dataset.buyButtonsUnavailable = 'false';
    return;
  }

  const atcButton = /** @type {HTMLButtonElement|null} */ (atcWrapper.querySelector('[name="add"]'));
  if (!atcButton) return;

  const atcText = safeTranslation(blockEl.dataset.atcText);
  const soldOutText = safeTranslation(blockEl.dataset.soldOutText);
  // PDP `data-unavailable-text` is now sourced from `products.product.unavailable_for_pickup`
  // ("Not available for pickup at your selected bakery") via blocks/buy-buttons.liquid.
  // If that key is absent (or any other data-* translation is missing) `safeTranslation`
  // returns '' and the `|| soldOutText` cascade kicks in — preventing the raw
  // `Translation missing: <key>` sentinel from rendering inside the disabled ATC button
  // (see screenshot referenced in the locale-fallback chat).
  const unavailableText = safeTranslation(blockEl.dataset.unavailableText) || soldOutText;

  const mf16Ids = parseMf16Ids(blockEl.dataset.availabilityMf16);
  const mf20Stock = parseMf20Stock(blockEl.dataset.availabilityMf20);
  const mf34PreorderAllowed = parseMf34PreorderAllowed(blockEl.dataset.availabilityMf34);
  const mf36StockSynced = parseMf36StockSynced(blockEl.dataset.availabilityMf36);
  const bakeryKey = getSelectedAvailabilityBakeryId(selection);
  const pickupDay = getSelectedPickupDay(selection);
  const { state } = getAvailabilityState(
    mf16Ids,
    mf20Stock,
    bakeryKey,
    pickupDay,
    mf34PreorderAllowed,
    mf36StockSynced
  );

  // MF34 preorder gate is the BLOCKING reason only when the product would
  // otherwise be purchasable for tomorrow (MF16 hit) — an MF16 miss keeps the
  // standard "Not available at {bakery}" copy regardless of MF34.
  const preorderBlocked =
    mf34PreorderAllowed === false &&
    pickupDay === 'tomorrow' &&
    getAvailabilityState(mf16Ids, mf20Stock, bakeryKey, pickupDay, null, mf36StockSynced).state ===
      'available';

  // MF16 miss with a bakery selected: the product isn't carried at the chosen
  // bakery, so there's nothing to purchase. Hide the entire purchase form
  // (the CSS in blocks/buy-buttons.liquid keys off this flag) rather than
  // showing a disabled "Unavailable for Click and Collect" ATC + hint. The
  // block's data-* stay intact so a later selection change re-runs this and
  // un-hides the form when the product IS available. `preorderBlocked` (MF34
  // "Same-day Only") keeps its dedicated locked-form treatment below.
  const mf16Miss = state === 'unavailable' && !preorderBlocked;
  blockEl.dataset.buyButtonsUnavailable = mf16Miss ? 'true' : 'false';
  if (mf16Miss) {
    delete atcButton.dataset.atcLoading;
    return;
  }

  if (variantAvailable === null) {
    variantAvailable = getVariantAvailableFromBlock(blockEl);
  }

  delete atcButton.dataset.atcLoading;

  const textEl = /** @type {HTMLElement|null} */ (atcButton.querySelector('.add-to-cart-text__content'));

  const variantBlocksAtc = variantAvailable === false;
  const canEnableAtc = state === 'available' && !variantBlocksAtc;

  // Sold out (true MF20 sell-through) and Shopify variant-level unavailability both surface as
  // "sold_out" in the hint, since both mean the customer can't get this exact variant today.
  // MF34 preorder-blocked gets its own hint state so the copy explains the actual reason
  // ("Available for same-day purchase only") instead of implying a stock/range problem.
  const hintState =
    state === 'available' && !variantBlocksAtc
      ? 'available'
      : preorderBlocked
        ? 'preorder_blocked'
        : state === 'sold_out' || variantBlocksAtc
          ? 'sold_out'
          : 'unavailable';

  if (canEnableAtc) {
    atcButton.disabled = false;
    if (textEl) textEl.textContent = atcText;
    else atcButton.textContent = atcText;
    setQuantityControlsDisabled(quantityEl, false);
  } else {
    atcButton.disabled = true;
    // Preorder-blocked keeps the regular "Add to cart" label (disabled) — the
    // same-day-only hint underneath carries the explanation, and the stock-
    // flavoured sold-out / unavailable labels would misstate the reason.
    const label =
      hintState === 'preorder_blocked'
        ? atcText
        : hintState === 'sold_out'
          ? soldOutText
          : unavailableText;
    if (textEl) textEl.textContent = label;
    else atcButton.textContent = label;
    // Keep the selector visible so the layout stays stable; just lock interaction.
    setQuantityControlsDisabled(quantityEl, true);
  }

  setPreparationControlsDisabled(blockEl, preorderBlocked);
  // "Online order now, pick up later" contradicts a locked preorder form.
  setBuyButtonsHeadingHidden(blockEl, preorderBlocked);

  syncClickCollectHint(blockEl, hintState, selection, pickupDay);
}

/**
 * Show/hide the merchant-configured buy-buttons heading ("Online order now,
 * pick up later"). Hidden for the MF34 preorder-blocked state only — the copy
 * promises online ordering while the form is locked. `.buy-buttons-heading`
 * sets no `display`, so the UA `[hidden] { display: none }` rule applies.
 *
 * @param {HTMLElement} blockEl
 * @param {boolean} hidden
 */
function setBuyButtonsHeadingHidden(blockEl, hidden) {
  const headings = blockEl.querySelectorAll('[data-pdp-buy-buttons-heading]');
  for (const heading of headings) {
    /** @type {HTMLElement} */ (heading).hidden = hidden;
  }
}

/**
 * Hard-coded English fallback templates for the click-and-collect hint copy.
 * Used when the Liquid-side `| t` lookup returns blank or Shopify's
 * `Translation missing: <key>` sentinel (typical causes: a partial
 * localisation in a non-English store, a locale-file edit the Shopify CLI
 * dev server hasn't picked up yet, or a future key rename that orphans
 * the data-attribute consumer).
 *
 * Without these fallbacks `safeTranslation` would scrub the sentinel down
 * to `''` and the hint would silently disappear under the ATC button —
 * which is what just happened in dev when the locale file was edited but
 * `shopify theme dev` hadn't been restarted. Showing a sensible English
 * default is strictly better UX than hiding the hint entirely; merchants
 * who localise will override these via the `products.product.click_and_collect_*`
 * keys in `locales/en.default.json` and friends.
 *
 * Keep the `{{ bakery }}` placeholder syntax in lock-step with what the
 * `replace(...)` call below interpolates and what the Liquid renderers in
 * `blocks/buy-buttons.liquid` emit.
 *
 * @type {{
 *   today: string,
 *   tomorrow: string,
 *   soldOutToday: string,
 *   soldOutTomorrow: string,
 *   unavailable: string,
 *   sameDayOnly: string,
 * }}
 */
const HINT_FALLBACK_TEMPLATES = {
  today: 'Pick up from {{ bakery }} today',
  tomorrow: 'Pick up from {{ bakery }} tomorrow',
  soldOutToday: 'Sold out today at {{ bakery }}',
  soldOutTomorrow: 'Sold out tomorrow at {{ bakery }}',
  unavailable: 'Not available at {{ bakery }}',
  sameDayOnly: 'Available for same-day purchase only',
};

/**
 * Render the small bakery-aware hint under the ATC button. Three states map to three copy
 * variants (templates passed as `data-click-collect-*-text` attrs on the block, each containing
 * a `{{ bakery }}` placeholder for client-side interpolation):
 *
 *   available        → "Click-and-collect from {bakery} {today|tomorrow}"
 *   sold_out         → "Sold out at {bakery} {today|tomorrow}"
 *   unavailable      → "Not available at {bakery}"  (no day — product isn't carried here at all)
 *   preorder_blocked → "Available for same-day purchase only"  (MF34 false + Tomorrow pickup)
 *
 * Hidden when there's no bakery name to interpolate (no selection yet) so we never render
 * "Click-and-collect from  today". If the Liquid-rendered template is blank or scrubbed
 * down to '' by `safeTranslation` (e.g. missing locale key, CLI cache lag), falls back to
 * `HINT_FALLBACK_TEMPLATES` so the hint always renders something useful.
 *
 * @param {HTMLElement} blockEl
 * @param {'available' | 'sold_out' | 'unavailable' | 'preorder_blocked'} hintState
 * @param {{ name?: string } | null} selection
 * @param {string | null} pickupDay
 */
function syncClickCollectHint(blockEl, hintState, selection, pickupDay) {
  const hintEl = /** @type {HTMLElement|null} */ (
    blockEl.querySelector('[data-pdp-click-collect-hint]')
  );
  if (!hintEl) return;

  const bakeryName = String(selection?.name || '').trim();
  if (!bakeryName) {
    hintEl.hidden = true;
    hintEl.textContent = '';
    return;
  }

  const isTomorrow = String(pickupDay || '').toLowerCase() === 'tomorrow';
  let template = '';
  let fallback = '';
  if (hintState === 'sold_out') {
    template = isTomorrow
      ? safeTranslation(blockEl.dataset.clickCollectSoldOutTomorrowText)
      : safeTranslation(blockEl.dataset.clickCollectSoldOutTodayText);
    fallback = isTomorrow
      ? HINT_FALLBACK_TEMPLATES.soldOutTomorrow
      : HINT_FALLBACK_TEMPLATES.soldOutToday;
  } else if (hintState === 'preorder_blocked') {
    template = safeTranslation(blockEl.dataset.clickCollectSameDayOnlyText);
    fallback = HINT_FALLBACK_TEMPLATES.sameDayOnly;
  } else if (hintState === 'unavailable') {
    template = safeTranslation(blockEl.dataset.clickCollectUnavailableText);
    fallback = HINT_FALLBACK_TEMPLATES.unavailable;
  } else {
    template = isTomorrow
      ? safeTranslation(blockEl.dataset.clickCollectTomorrowText)
      : safeTranslation(blockEl.dataset.clickCollectTodayText);
    fallback = isTomorrow
      ? HINT_FALLBACK_TEMPLATES.tomorrow
      : HINT_FALLBACK_TEMPLATES.today;
  }

  // Liquid-rendered template wins when present; otherwise the English fallback
  // covers the missing-locale-key case (`safeTranslation` returns '' for both
  // blank values and the `Translation missing: <key>` sentinel). The hint is
  // never silently hidden when a bakery is selected — that was the bug
  // surfaced in the "Where has the messaging under the ATC button gone?"
  // screenshot: sentinel scrubbed → empty template → hint disappeared.
  const resolved = template || fallback;
  if (!resolved) {
    hintEl.hidden = true;
    hintEl.textContent = '';
    return;
  }

  hintEl.textContent = resolved.replace('{{ bakery }}', bakeryName);
  hintEl.hidden = false;
}

/**
 * Toggle `disabled` on the preparation selector's radio inputs and stamp
 * `data-disabled` on the `<product-preparation-selector>` root for CSS
 * dimming (snippets/product-preparation-selector.liquid). Only the MF34
 * preorder-blocked case locks preparations — sold_out / unavailable states
 * keep them interactive, matching pre-MF34 behaviour.
 *
 * The selector renders inside the block's product form, so scoping the query
 * to `blockEl` covers both the PDP and the quick-add flyout morph.
 *
 * @param {HTMLElement} blockEl
 * @param {boolean} disabled
 */
function setPreparationControlsDisabled(blockEl, disabled) {
  const prepEls = blockEl.querySelectorAll('product-preparation-selector');
  for (const prepEl of prepEls) {
    const el = /** @type {HTMLElement} */ (prepEl);
    el.dataset.disabled = disabled ? 'true' : 'false';
    const inputs = el.querySelectorAll('input');
    for (const input of inputs) {
      if (input instanceof HTMLInputElement) input.disabled = disabled;
    }
  }
}

/**
 * Toggle `disabled` on every input/button inside a `.quantity-selector` and set
 * `data-disabled` on the wrapper for CSS dimming.
 *
 * @param {HTMLElement | null} quantityEl
 * @param {boolean} disabled
 */
function setQuantityControlsDisabled(quantityEl, disabled) {
  if (!quantityEl) return;
  quantityEl.dataset.disabled = disabled ? 'true' : 'false';
  const controls = quantityEl.querySelectorAll('input, button');
  for (const ctrl of controls) {
    if (ctrl instanceof HTMLInputElement || ctrl instanceof HTMLButtonElement) {
      ctrl.disabled = disabled;
    }
  }
}

/**
 * Re-syncs every preparation group's hidden pseudo-PLU line-item property
 * (`[data-preparation-plu-input]`) from its currently-checked radio. The
 * delegated `change` listener below handles live user changes; this covers
 * post-morph states (quick-add flyout injection, variant:update section
 * morphs) where the checked radio and the Liquid-seeded hidden value can
 * momentarily disagree.
 */
function syncPreparationPluInputs() {
  const fieldsets = document.querySelectorAll(
    'product-preparation-selector .product-preparation__fieldset'
  );
  for (const fieldset of fieldsets) {
    const pluInput = fieldset.querySelector('[data-preparation-plu-input]');
    if (!(pluInput instanceof HTMLInputElement)) continue;
    const checked = fieldset.querySelector('.product-preparation__input:checked');
    if (checked instanceof HTMLInputElement) {
      pluInput.value = checked.dataset.pseudoPlu || '';
    }
  }
}

/**
 * Re-applies buy-button state for all `[data-buy-buttons-block]` nodes (PDP + quick-add flyout).
 *
 * @param {CustomEvent | null} [variantUpdateEvent]
 */
export function initAllBuyButtonsBlocks(variantUpdateEvent = null) {
  const resource = variantUpdateEvent?.detail?.resource;
  const variantAvailable =
    resource && typeof resource.available === 'boolean' ? resource.available : null;
  const productId = variantUpdateEvent?.detail?.data?.productId;

  const blocks = document.querySelectorAll('[data-buy-buttons-block]');
  for (const blockEl of blocks) {
    const el = /** @type {HTMLElement} */ (blockEl);
    const form = el.querySelector('product-form-component');
    if (productId && form?.dataset.productId && form.dataset.productId !== productId) {
      continue;
    }

    applyBuyButtonsState(el, variantAvailable);
  }

  syncPreparationPluInputs();
}

document.addEventListener('DOMContentLoaded', function () {
  initAllBuyButtonsBlocks();

  const stateApi = /** @type {any} */ (window).StoreSelectionState;

  if (stateApi && typeof stateApi.subscribe === 'function') {
    stateApi.subscribe(() => initAllBuyButtonsBlocks());
  } else {
    window.addEventListener('store-selection:changed', () => initAllBuyButtonsBlocks());
    window.addEventListener('store-selection:cleared', () => initAllBuyButtonsBlocks());
  }
});

document.addEventListener('variant:update', (event) => {
  initAllBuyButtonsBlocks(/** @type {CustomEvent} */ (event));
});

// Keep each preparation group's hidden pseudo-PLU line-item property
// (`properties[_pseudoPLU]`, snippets/product-preparation-selector.liquid)
// in sync with the checked radio. Delegated at document level so it covers
// the PDP form AND the quick-add flyout morph, where inline scripts inserted
// via innerHTML never execute. Liquid seeds the hidden input with the
// default-checked option's PLU, so this only needs to run on user changes.
document.addEventListener('change', (event) => {
  const radio = event.target;
  if (!(radio instanceof HTMLInputElement)) return;
  if (radio.type !== 'radio' || !radio.classList.contains('product-preparation__input')) return;

  const fieldset = radio.closest('.product-preparation__fieldset');
  const pluInput = fieldset?.querySelector('[data-preparation-plu-input]');
  if (pluInput instanceof HTMLInputElement) {
    pluInput.value = radio.dataset.pseudoPlu || '';
  }
});
